<script lang="ts">
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import ReviewRatingStars from "@apps/reviews/components/RatingStars/ReviewRatingStars.svelte";
    import ReviewsEditButton from "@apps/reviews/components/ReviewsEditButton.svelte";
    import ImportedReview from "@apps/reviews/components/ReviewsMeta/ImportedReview.svelte";
    import Incentivized from "@apps/reviews/components/ReviewsMeta/IncentivizedReview.svelte";
    import VerifiedBuyer from "@apps/reviews/components/ReviewsMeta/VerifiedBuyer.svelte";
    import ToggleVote from "@apps/reviews/components/ToggleVote.svelte";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { VotesState } from "@apps/reviews/stores/VotesStore";
    import { getHumanReadableDate } from "@lib/utils";

    import Badge from "@components/Badge/Badge.svelte";
    import ClockIcon from "@components/icons/ClockIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import EmblaSliderDots from "@components/Slider/EmblaSliderDots.svelte";
    import type { EmblaSliderApi } from "@components/Slider/types";
    import T from "@components/T.svelte";

    import ReviewsProduct from "../../ReviewsProduct.svelte";
    import ReviewsUnavailableProduct from "../../ReviewsUnavailableProduct.svelte";
    import ReviewsMasonryItemFrame from "./ReviewsMasonryItemFrame.svelte";

    export let namespace: string;
    export let review: ReviewEntity;
    export let customerName: string;
    export let showTitle: boolean;
    export let showProducts: boolean;
    export let votesEnabled: boolean;
    export let reviewVotes: VotesState | undefined;
    export let isVerifiedBuyerLabelEnabled: boolean;
    export let isVerifiedBuyerByMerchantLabelEnabled: boolean;
    export let isImportedEnabled: boolean;
    export let isIncentivizedEnabled: boolean;
    export let isReviewEditingEnabled: boolean;

    let emblaApi: EmblaSliderApi | null = null;
    let currentSlide = 0;
    let totalSlides = 0;

    function handleSelect() {
        if (emblaApi) {
            currentSlide = emblaApi.selectedScrollSnap();
        }
    }

    function handleReady() {
        if (emblaApi) {
            totalSlides = review.images.length;
            currentSlide = emblaApi.selectedScrollSnap();
        }
    }
</script>

<ReviewsMasonryItemFrame on:openReview>
    <svelte:fragment slot="media">
        <div class="gw-rv-item__media" role="button" tabindex="0">
            {#if review.images.length}
                <EmblaSlider
                    setApi={(api) => (emblaApi = api)}
                    perPage={1}
                    loop={false}
                    controls={false}
                    on:select={handleSelect}
                    on:ready={handleReady}
                >
                    <svelte:fragment slot="slides">
                        {#each review.images as image (image.id)}
                            <div class="gw-rv-item__media-img-wrapper">
                                <Image
                                    ratio={282 / 351}
                                    alt={review.title || ""}
                                    src={getImageSizeUrl(image.sizes, "md") ||
                                        ""}
                                    objectFit="cover"
                                    on:imageLoaded
                                />
                            </div>
                        {/each}
                    </svelte:fragment>

                    <EmblaSliderDots
                        slot="dots"
                        perPage={1}
                        currentIndex={currentSlide}
                        slidesCount={totalSlides}
                        api={emblaApi || undefined}
                        position="absolute"
                        bottom="12px"
                    />
                </EmblaSlider>
            {/if}
        </div>
    </svelte:fragment>

    <svelte:fragment slot="published">
        <div class="gw-rv-item__badges">
            {#if isImportedEnabled && review.isImported}
                <ImportedReview {namespace} />
            {/if}
            {#if isIncentivizedEnabled && review.isIncentivized}
                <Incentivized {namespace} />
            {/if}
        </div>

        {#if !review.isPublished}
            <Badge icon={ClockIcon} size="lg" variant="flat" transparent>
                <T {namespace} translationKey="under_review" />
            </Badge>
        {/if}

        {#if isReviewEditingEnabled && review.isPublished}
            <ReviewsEditButton {namespace} on:editReview />
        {/if}
    </svelte:fragment>

    <ReviewRatingStars
        width={"16"}
        height={"16"}
        rating={review.rating}
        slot="rating"
    />

    <span class="gw-rv-item__date" slot="date">
        {getHumanReadableDate(review.createdAt)}
    </span>

    <svelte:fragment slot="username">
        <span class="gw-rv-item__username">
            {customerName}
        </span>
        {#if (isVerifiedBuyerLabelEnabled && review.customer?.isVerifiedBuyer) || (isVerifiedBuyerByMerchantLabelEnabled && review.customer?.isVerifiedBuyerByMerchant)}
            <VerifiedBuyer
                {namespace}
                isVerifiedBuyer={review.customer.isVerifiedBuyer}
                isVerifiedBuyerByMerchant={review.customer
                    .isVerifiedBuyerByMerchant}
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="title">
        {#if (showTitle && review.title) || (review.isIncentivized && isIncentivizedEnabled) || (isImportedEnabled && review.isImported)}
            {#if showTitle && review.title}
                <h3 class="gw-rv-item__title">{review.title}</h3>
            {/if}
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        {review.body}
    </svelte:fragment>

    <svelte:fragment slot="vote">
        {#if votesEnabled && reviewVotes}
            <ToggleVote {reviewVotes} on:toggleVote />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="footer">
        {#if review.product?.isDraft || review.product?.isDeleted}
            <ReviewsUnavailableProduct
                productId={review.product.id}
                token={review.product?.unavailableProductToken || ""}
                on:productReady
            />
        {:else if review.product?.id && showProducts}
            <ReviewsProduct
                productId={review.product.id}
                on:productReady
                on:imageLoaded
            />
        {/if}
    </svelte:fragment>
</ReviewsMasonryItemFrame>

<style lang="postcss">
    .gw-rv-item {
        &__username {
            font-size: 1em;
            font-weight: 600;
        }

        &__date {
            color: var(--gw-lightgrey-text-color);
            font-size: 0.75em;
        }

        &__title {
            width: 100%;
            margin: 0;
            font-size: 1em;
            font-weight: 600;
            line-height: 20px;
        }

        &__badges {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex: 1;
            gap: 0.5em;
        }
    }
</style>
